import _ from 'underscore';

import stringify from 'json-stable-stringify';

import { get, set } from '@bingads-webui-universal/primitive-utilities';

import { TimeAwareCache, LRUReplacementStrategy } from '@bingads-webui-universal/time-aware-cache';

import { CACHE_SIZE, DEFAULT_TTL } from './constants';
import {
  entityCacheKeyMatchesId,
  parseEntityCacheKey,
} from './entity-cache-key';
import type {
  EntityKey,
  IAnyObject,
  IPropertyPath,
  ICacheItem,
  IObservedCache,
  PropertyPathGetter,
  PropertyPathSetter,
} from './types';

const getByPropertyPath = get as PropertyPathGetter;
const setByPropertyPath = set as PropertyPathSetter;

function deletePropertyPath(obj: IAnyObject, path: IPropertyPath) {
  let o = obj;
  const { length } = path;
  for (let i = 0; i < length - 1; i += 1) {
    o = o[path[i]] as IAnyObject;

    if (typeof o === 'undefined') {
      return;
    }
  }
  delete o[path[length - 1]];
}

export class ObservableCache {
  observedCache: IObservedCache = {};

  noObservers = new TimeAwareCache<ICacheItem>({
    ttl: DEFAULT_TTL,
    strategy: new LRUReplacementStrategy({ limit: CACHE_SIZE }),
  });

  set(path: IPropertyPath, item: ICacheItem) {
    if (item.hasObservers()) {
      setByPropertyPath(this.observedCache, path, item);
    } else {
      this.noObservers.set(stringify(path), item);
    }
  }

  get(path: IPropertyPath): ICacheItem | undefined {
    const observedItem = getByPropertyPath(this.observedCache, path) as ICacheItem | undefined;
    return observedItem || this.noObservers.get(stringify(path)) || undefined;
  }

  observerAdded(item: ICacheItem, path: IPropertyPath) {
    if (item.observers.size === 1) {
      this.noObservers.del(stringify(path));
      setByPropertyPath(this.observedCache, path, item);
    }
  }

  observerReduced(item: ICacheItem, path: IPropertyPath) {
    if (item.observers.size === 0) {
      deletePropertyPath(this.observedCache, path);
      this.noObservers.set(stringify(path), item);
    }
  }

  invalidateObservedCache(typeName: string, skipEntityCallPaths: string[] | null = null) {
    const clearCacheKeys: string[] = [];
    _.map(this.observedCache[typeName], (cacheItems: Record<string, ICacheItem>, subKey: string) => {
      const { idOrPath, resourcePath } = parseEntityCacheKey(subKey);
      const skipCall = skipEntityCallPaths && _.find(
        skipEntityCallPaths,
        (path: string) => resourcePath.includes(path) || `${typeName}s(${idOrPath})`.includes(path)
      );
      if (skipCall) {
        clearCacheKeys.push(subKey);
      } else {
        _.each(cacheItems, (cacheItem: ICacheItem) => {
          cacheItem.invalidate();
        });
      }
    });
    clearCacheKeys.forEach((key: string) => {
      delete this.observedCache[typeName][key];
    });
  }

  invalidateObservedCacheById(
    typeName: string,
    id: EntityKey,
    stringifiedOptions: string,
    skipEntityCallPaths: string[] | null = null,
    preservedEntityCacheKey: string | null = null
  ) {
    const clearCacheKeys: string[] = [];
    _.each(this.observedCache[typeName], (cacheItems: Record<string, ICacheItem>, cacheKey: string) => {
      if (entityCacheKeyMatchesId(cacheKey, id)) {
        const { resourcePath } = parseEntityCacheKey(cacheKey);
        const skipCall = skipEntityCallPaths && _.find(
          skipEntityCallPaths,
          (path: string) => resourcePath.includes(path) || `${typeName}s(${id})`.includes(path)
        );
        if (skipCall) {
          clearCacheKeys.push(cacheKey);
        } else {
          _.each(cacheItems, (cacheItem: ICacheItem, options: string) => {
            const isPreservedEntry = preservedEntityCacheKey == null
              ? options === stringifiedOptions
              : cacheKey === preservedEntityCacheKey && options === stringifiedOptions;
            if (!isPreservedEntry) {
              cacheItem.invalidate();
            }
          });
        }
      }
    });
    clearCacheKeys.forEach((cacheKey: string) => {
      delete this.observedCache[typeName][cacheKey];
    });
  }

  invalidateNoObserverCache(key: string) {
    const cache = this.noObservers.get(key);

    if (cache) {
      cache.invalidate();
    }
  }
}
